import React, { useState } from "react";
import { Download, FileText } from "lucide-react";
import { api, API } from "@/lib/api";
import { useList } from "@/hooks/useApi";
import { PageHeader, Loading, EmptyState, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const REPORTS = [
  { key: "production", label: "Daily Production" },
  { key: "orders", label: "Order-wise" },
  { key: "qc", label: "QC Defects" },
  { key: "packing", label: "Packing" },
  { key: "dispatch", label: "Dispatch" },
];

export default function Reports() {
  const [type, setType] = useState("production");
  const { data, isLoading } = useList(`report-${type}`, `/reports/${type}`);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/reports/${type}`, { params: { export: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${type}_report.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div>
      <PageHeader title="Reports & Analytics" subtitle="Filter, review and export operational reports" testid="reports-header">
        <Button onClick={exportCsv} variant="outline" className="gap-2" data-testid="export-csv-btn"><Download className="w-4 h-4" /> Export CSV</Button>
      </PageHeader>
      <Tabs value={type} onValueChange={setType}>
        <TabsList className="flex flex-wrap h-auto gap-1 mb-4">
          {REPORTS.map((r) => <TabsTrigger key={r.key} value={r.key} data-testid={`report-tab-${r.key}`}>{r.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <Card className="overflow-hidden">
        {isLoading ? <Loading /> : !data?.rows?.length ? (
          <EmptyState title="No data for this report" hint="Seed demo data or record production first." />
        ) : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow>{data.fields.map((f) => <TableHead key={f} className="whitespace-nowrap capitalize">{f.replace(/_/g, " ")}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{data.rows.slice(0, 200).map((row, i) => (
              <TableRow key={i} data-testid={`report-row-${i}`}>
                {data.fields.map((f) => <TableCell key={f} className="whitespace-nowrap text-sm">{typeof row[f] === "number" ? fmt(row[f]) : (row[f] ?? "—")}</TableCell>)}
              </TableRow>
            ))}</TableBody>
          </Table></div>
        )}
      </Card>
      <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Showing up to 200 rows. Use Export CSV for the full dataset.</p>
    </div>
  );
}
